// Price-changes route: week-over-week price movements (the "Fiyat Degisimi" ask).
// Shows plans whose price changed vs the prior verified week. Plans with no
// prior-week price are listed at the bottom as "no prior data" — never faked.

import React, { useEffect, useMemo, useState } from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { api } from '../lib/api';
import type { PriceChangesData, PriceChangeRow } from '../lib/api';
import { formatUSD, formatGB, formatDays } from '../lib/format';
import { formatWeekRange } from '../components/Header';

interface PriceChangesProps {
  lang: Lang;
  currentWeek: number | null;
  code: string;
}

function ChangeCell({ row, lang }: { row: PriceChangeRow; lang: Lang }) {
  if (row.status === 'no_prior' || row.change_pct == null) {
    return <span className="text-xs text-neutral-400">{t('noPriorData', lang)}</span>;
  }
  const up = row.change_pct > 0;
  // Price up = worse for the buyer (rose); price down = cheaper (emerald).
  // Likely-noise rows (huge swings, usually a source swap) are shown muted with a flag.
  const cls = row.likely_noise
    ? 'text-neutral-500 bg-neutral-50 border-neutral-200'
    : up
      ? 'text-rose-700 bg-rose-50 border-rose-200'
      : 'text-emerald-700 bg-emerald-50 border-emerald-200';
  const sign = up ? '+' : '';
  const arrow = up ? '▲' : '▼';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`}>
        {arrow} {sign}{row.change_pct.toFixed(1)}%
      </span>
      {row.likely_noise && (
        <span className="text-[10px] text-amber-600" title={t('likelyNoiseHint', lang)}>{t('likelyNoise', lang)}</span>
      )}
    </span>
  );
}

export function PriceChanges({ lang, currentWeek, code }: PriceChangesProps) {
  const [data, setData] = useState<PriceChangesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [region, setRegion] = useState('all');

  useEffect(() => {
    if (!currentWeek) return;
    setLoading(true);
    setError(null);
    api.priceChanges
      .byWeek(currentWeek, 'all')
      .then(setData)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWeek]);

  const regionOptions = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    for (const c of data.changes) if (c.region) set.add(c.region);
    return Array.from(set).sort();
  }, [data]);

  const changes = useMemo(() => {
    if (!data) return [];
    if (region === 'all') return data.changes;
    return data.changes.filter((c) => c.region === region);
  }, [data, region]);

  if (!currentWeek || loading) {
    return <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">{t('loading', lang)}</div>;
  }
  if (error) {
    return <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">{error}</div>;
  }

  const realChanges = changes.filter((c) => c.status !== 'no_prior').length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
          {t('priceChangesTitle', lang)}
        </h1>
        <span className="text-xs text-neutral-500 tabular-nums">
          {t('weekLabel', lang)} {currentWeek % 100}
          {data?.prior_week && (
            <span className="text-neutral-300"> · vs {t('weekLabel', lang)} {data.prior_week % 100}</span>
          )}
        </span>
      </div>

      <div className="card p-4">
        <p className="text-xs text-neutral-600 leading-relaxed">{t('priceChangesIntro', lang)}</p>
      </div>

      {!data?.has_prior ? (
        <div className="card p-8 text-center text-neutral-500 text-sm">{t('priceChangesNoData', lang)}</div>
      ) : (
        <>
          {regionOptions.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-neutral-500">{t('colCountry', lang)}:</span>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="text-xs border border-neutral-300 rounded px-2 py-1 bg-white text-neutral-800"
              >
                <option value="all">{t('allRegions', lang)}</option>
                {regionOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          {changes.length === 0 ? (
            <div className="card p-8 text-center text-neutral-500 text-sm">{t('noData', lang)}</div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-200 text-neutral-500 text-left">
                    <th className="px-3 py-2 font-medium">{t('colProvider', lang)}</th>
                    <th className="px-3 py-2 font-medium">{t('colCountry', lang)}</th>
                    <th className="px-3 py-2 font-medium">{t('colData', lang)}</th>
                    <th className="px-3 py-2 font-medium">{t('colDays', lang)}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('colOldPrice', lang)}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('colNewPrice', lang)}</th>
                    <th className="px-3 py-2 font-medium">{t('colChange', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-3 py-2 text-neutral-800">
                        <a
                          href={`/providers/${encodeURIComponent(c.provider_id)}?code=${code}`}
                          className="hover:text-emerald-600 underline decoration-dotted decoration-neutral-300"
                        >
                          {c.provider_name}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-neutral-600">{c.region ?? '–'}</td>
                      <td className="px-3 py-2 text-neutral-600">
                        {c.data_unlimited === 1 ? t('unlimited', lang) : formatGB(c.data_gb)}
                      </td>
                      <td className="px-3 py-2 text-neutral-600">{formatDays(c.validity_days)}</td>
                      <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">
                        {c.old_price_usd != null ? formatUSD(c.old_price_usd) : '–'}
                      </td>
                      <td className="px-3 py-2 text-right text-neutral-800 tabular-nums">{formatUSD(c.new_price_usd)}</td>
                      <td className="px-3 py-2"><ChangeCell row={c} lang={lang} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
